import React, { useCallback, useState } from "react";
import { useSnackbar } from "notistack";
import moment from "moment";
import Tooltip from "@mui/material/Tooltip";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import CalendarIcon from "@mui/icons-material/CalendarToday";
import { DatePicker } from "../../components";
import { RequestIdChipInput } from "./RequestIdChipInput";
import {
  FilterBarContainer,
  OtherFilters,
  AdvancedToggleBtn,
  PillDate,
  PillContainer,
  SearchBox,
  TextInput
} from "./styles";

function FilterBar(props) {
  const { startDate, endDate, setQueries } = props;

  const [beforeDate, setBeforeDate] = useState(startDate);
  const [afterDate, setAfterDate] = useState(endDate);

  const [openBeforeDate, setOpenBeforeDate] = useState(false);
  const [openAfterDate, setOpenAfterDate] = useState(false);
  const [userIdQuery, setUserIdQuery] = useState("");
  const [projectIdQuery, setProjectIdQuery] = useState("");
  const [requestIds, setRequestIds] = useState([]);
  const [showAdvancedFilter, setShowAdvancedFilter] = useState(false);

  const { enqueueSnackbar } = useSnackbar();

  const setOpenBeforeDateHandler = useCallback((open) => {
    setOpenBeforeDate(open);
    setOpenAfterDate(false);
  }, []);

  const setOpenAfterDateHandler = useCallback((open) => {
    setOpenBeforeDate(false);
    setOpenAfterDate(open);
  }, []);

  const setBeforeDateField = useCallback((date) => {
    setBeforeDate(date);
    setOpenBeforeDate(false);
    setOpenAfterDate(false);
  }, []);

  const setAfterDateField = useCallback((date) => {
    setAfterDate(date);
    setOpenBeforeDate(false);
    setOpenAfterDate(false);
  }, []);

  const formatDate = useCallback((date) => {
    return moment(date).format("MMM DD, YYYY h:mm:ss a");
  }, []);

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === "Enter") {
        setQueries({ projectIdQuery, userIdQuery, requestIds });
      }
    },
    [projectIdQuery, requestIds, setQueries, userIdQuery]
  );

  const handleChipChange = useCallback((value) => {
    setRequestIds(value);
  }, []);

  const setFilter = useCallback(() => {
    setQueries({ projectIdQuery, beforeDate, afterDate, userIdQuery, requestIds });
  }, [beforeDate, afterDate, projectIdQuery, userIdQuery, setQueries, requestIds]);

  return (
    <FilterBarContainer>
      <OtherFilters>
        <div className="filter">
          <div className="filter-label">By Project:</div>
          <SearchBox widthSize="large">
            <div className="search-icon">
              <SearchIcon fontSize="small" />
            </div>
            <TextInput
              value={projectIdQuery}
              onChange={(e) => setProjectIdQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search by project id..."
              inputProps={{ "aria-label": "search" }}
            />
          </SearchBox>
        </div>
        <div className="filter">
          <div className="filter-label">By User:</div>
          <SearchBox>
            <div className="search-icon">
              <SearchIcon fontSize="small" />
            </div>
            <TextInput
              value={userIdQuery}
              onChange={(e) => setUserIdQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search by user id..."
              inputProps={{ "aria-label": "search" }}
            />
          </SearchBox>
        </div>
        <div className="filter">
          <div className="filter-label">Filter by date:</div>
          <div className="flex-row">
            <PillContainer>
              <PillDate onClick={() => setOpenBeforeDateHandler(!openBeforeDate)}>
                <CalendarIcon />
                {formatDate(beforeDate)}
              </PillDate>
              <DatePicker
                defaultValue={new Date(beforeDate)}
                open={openBeforeDate}
                setDateOpen={setOpenBeforeDate}
                setDateField={setBeforeDateField}
                maxDate={afterDate ? new Date(afterDate) : null}
              />
            </PillContainer>
            <PillContainer>
              <PillDate onClick={() => setOpenAfterDateHandler(!openAfterDate)}>
                <CalendarIcon /> {formatDate(afterDate)}
              </PillDate>
              <DatePicker
                defaultValue={new Date(afterDate)}
                open={openAfterDate}
                setDateOpen={setOpenAfterDate}
                setDateField={setAfterDateField}
                minDate={beforeDate ? new Date(beforeDate) : null}
              />
            </PillContainer>
          </div>
        </div>
        <button className="filter-btn" onClick={setFilter}>
          Set Filter
        </button>
        <Tooltip title="Advanced Filters" arrow>
          <AdvancedToggleBtn
            toggled={showAdvancedFilter}
            onClick={() => {
              setShowAdvancedFilter(!showAdvancedFilter);
              if (!showAdvancedFilter) {
                enqueueSnackbar("When a request ID is specified, other filters are ignored!", { variant: "info" });
              }
            }}
          >
            <FilterListIcon />
          </AdvancedToggleBtn>
        </Tooltip>
      </OtherFilters>
      {showAdvancedFilter && (
        <div>
          <RequestIdChipInput onChange={handleChipChange} />
        </div>
      )}
    </FilterBarContainer>
  );
}

export default FilterBar;
